import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Pencil, Loader2, AlertTriangle } from "lucide-react";
import { RCA_CATEGORIES, RCA_STATUS, ACTION_STATUS, fmtDateTime, duration } from "@/lib/rca";
import { SeverityBadge, RcaStatusBadge } from "@/components/rca/RcaBits";
import { downloadRcaPdf } from "@/lib/rcaPdf";

function H({ children }) {
  return <h2 className="font-display text-base font-bold text-indigo-600 mt-8 mb-3 pb-1 border-b border-slate-100">{children}</h2>;
}
function Txt({ children }) {
  return <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{children || "—"}</p>;
}
function KV({ k, v }) {
  return <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</p><p className="text-sm text-slate-800 mt-0.5">{v || "—"}</p></div>;
}

export default function RcaReportPage() {
  const { rcaId } = useParams();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/rcas/${rcaId}`).then((x) => setR(x.data)); }, [rcaId]);
  if (!r) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  const draft = ["draft", "in_review"].includes(r.status);
  const exportPdf = async () => { setBusy(true); await downloadRcaPdf(r); setBusy(false); };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="rca-report-page">
      <div className="max-w-[860px] mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <Link to="/rca" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />All RCAs</Link>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild data-testid="report-edit-button"><Link to={`/rca/${r.id}`}><Pencil className="w-3.5 h-3.5 mr-1.5" />Edit</Link></Button>
            <Button size="sm" onClick={exportPdf} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="report-export-pdf-button">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Export PDF
            </Button>
          </div>
        </div>

        <article className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm px-5 sm:px-10 py-8 sm:py-10" data-testid="rca-report">
          {draft && <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center text-[110px] sm:text-[160px] font-black text-red-500/[0.06] -rotate-[30deg]" data-testid="report-draft-watermark">DRAFT</span>}
          <div className="relative">
            <div className="flex items-start gap-4 pb-5 border-b-2 border-indigo-600">
              <img src="/logo-mark.png" alt="" className="w-12 h-12 object-contain shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Root cause analysis report · Internal</p>
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1 break-words">{r.title}</h1>
                <div className="flex items-center gap-2 mt-2 flex-wrap"><span className="font-mono text-xs font-semibold text-slate-500">{r.key}</span><SeverityBadge sev={r.severity} /><RcaStatusBadge status={r.status} /></div>
              </div>
            </div>

            <H>Overview</H>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <KV k="Client" v={r.client_name} /><KV k="Responsible engineer" v={r.engineer_name} /><KV k="Status" v={RCA_STATUS[r.status]?.label} />
              <KV k="Created by" v={`${r.created_by_name} · ${fmtDateTime(r.created_at)}`} /><KV k="Approved by" v={r.approved_by && `${r.approved_by} · ${fmtDateTime(r.approved_at)}`} />
              <KV k="Linked" v={[...(r.tickets || []).map((t) => t.key), ...(r.tasks || []).map((t) => t.title)].join(", ")} />
            </div>
            <H>Executive summary</H><Txt>{r.summary}</Txt>

            <H>Incident window</H>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <KV k="Started" v={fmtDateTime(r.started_at)} /><KV k="Detected" v={fmtDateTime(r.detected_at)} /><KV k="Resolved" v={fmtDateTime(r.resolved_at)} />
              <KV k="Time to detect" v={duration(r.started_at, r.detected_at)} /><KV k="Time to resolve" v={duration(r.detected_at, r.resolved_at)} /><KV k="Total duration" v={duration(r.started_at, r.resolved_at)} />
            </div>
            <div className="mt-3"><KV k="Detection method" v={r.detection_method} /></div>

            <H>Impact</H><Txt>{r.impact}</Txt>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3"><KV k="Users / customers affected" v={r.customers_affected} /><KV k="Affected systems" v={(r.affected_systems || []).join(", ")} /></div>

            <H>Timeline</H>
            <ol className="relative border-l-2 border-slate-100 ml-1.5 space-y-3">
              {(r.timeline || []).map((t) => (
                <li key={t.id} className="pl-4 relative"><span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-white" />
                  <p className="text-[11px] font-mono text-slate-400">{fmtDateTime(t.at)}</p><p className="text-sm text-slate-700">{t.event}</p></li>
              ))}
              {!r.timeline?.length && <li className="pl-4 text-sm text-slate-400">No events recorded.</li>}
            </ol>

            <H>Root cause analysis — 5 Whys</H>
            <div className="space-y-2">
              {(r.whys || []).filter((w) => w.trim()).map((w, i) => (
                <div key={i} className="flex gap-3 text-sm" style={{ paddingLeft: `${Math.min(i, 4) * 12}px` }}><span className="font-bold text-indigo-600 w-12 shrink-0">Why {i + 1}</span><span className="text-slate-700">{w}</span></div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
              <KV k="Category" v={RCA_CATEGORIES[r.root_cause_category]} />
              <div className="sm:col-span-2"><KV k="Root cause statement" v={r.root_cause} /></div>
            </div>
            <div className="mt-3"><KV k="Contributing factors" v={r.contributing_factors} /></div>

            <H>Resolution & recovery</H><Txt>{r.resolution}</Txt>

            <H>Corrective & preventive actions ({r.actions_done}/{r.actions_total} done)</H>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[520px]">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left"><th className="p-2">Action</th><th className="p-2">Type</th><th className="p-2">Owner</th><th className="p-2">Due</th><th className="p-2">Status</th></tr></thead>
                <tbody>
                  {(r.actions || []).map((a) => (
                    <tr key={a.id} className="border-t border-slate-100"><td className="p-2 text-slate-800">{a.title}</td><td className="p-2 capitalize text-slate-600">{a.type}</td><td className="p-2 text-slate-600">{a.owner_name || "—"}</td><td className="p-2 text-slate-600">{a.due_date || "—"}</td>
                      <td className="p-2"><span className={`text-[11px] px-1.5 py-0.5 rounded ${ACTION_STATUS[a.status]?.chip}`}>{ACTION_STATUS[a.status]?.label}</span>{a.overdue && <AlertTriangle className="inline w-3.5 h-3.5 text-red-500 ml-1" />}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <H>Lessons learned</H><Txt>{r.lessons_learned}</Txt>

            <H>Review history</H>
            <ul className="space-y-1.5">{(r.history || []).map((h, i) => <li key={i} className="text-xs text-slate-600 flex gap-2 items-center flex-wrap"><RcaStatusBadge status={h.status} />{h.by} · {fmtDateTime(h.at)}{h.note && ` — “${h.note}”`}</li>)}</ul>
          </div>
        </article>
      </div>
    </div>
  );
}
